import { collection, addDoc, doc, updateDoc, deleteDoc, Timestamp, getDoc, query, where, getDocs } from 'firebase/firestore';
import { db, EmailService, generateSecureToken, hashInvitationToken } from '@municipality/firebase';
import { SuperAdminRole } from '@municipality/types';
import { NotificationService } from './notifications';

export interface SuperAdminInvitation {
  email: string;
  name: string;
  surname: string;
  phoneNumber: string;
  superAdminRole: SuperAdminRole;
  status: 'pending' | 'accepted' | 'expired';
  invitedBy: string;
  invitedAt: Date;
  expiresAt?: Date;
}

export interface SuperAdminUserUpdate {
  name?: string;
  surname?: string;
  phoneNumber?: string;
  superAdminRole?: SuperAdminRole;
  status?: 'active' | 'inactive' | 'pending';
}

export class SuperAdminInvitationService {
  /**
   * Send an invitation to a new super admin user
   */
  static async inviteUser(
    invitation: Omit<SuperAdminInvitation, 'status' | 'invitedAt'>,
    currentUserId: string
  ): Promise<void> {
    try {
      const plainToken = generateSecureToken();
      const hashedToken = await hashInvitationToken(plainToken);
      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

      const invitationData = {
        ...invitation,
        status: 'pending',
        role: 'super_admin', // All super admin users have this base role
        invitedBy: currentUserId,
        invitedAt: Timestamp.now(),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        token: hashedToken,
        tokenPlain: plainToken,
        expiresAt: Timestamp.fromDate(expiresAt),
      };

      await addDoc(collection(db, 'invitations'), invitationData);
      
      // Fetch inviter data for notifications and email
      const inviterRef = doc(db, 'users', currentUserId);
      const inviterSnap = await getDoc(inviterRef);
      const inviterData = inviterSnap.exists() ? inviterSnap.data() : null;
      const inviterRole = inviterData?.superAdminRole as SuperAdminRole | undefined;
      const inviterName = inviterData?.displayName || inviterData?.email || 'Super Admin';
      
      // Check if inviter is Region Manager inviting Content Manager or Support - notify Super Admins
      if (
        inviterRole === SuperAdminRole.REGION_MANAGER &&
        (invitation.superAdminRole === SuperAdminRole.CONTENT_MANAGER ||
         invitation.superAdminRole === SuperAdminRole.SUPPORT)
      ) {
        // Get Region Manager's assigned municipalities
        const assignedMunicipalities = (inviterData?.municipalityAssignments || [])
          .map((a: { municipalityId: string }) => a.municipalityId);

        // Fetch municipality names
        const municipalityNames: string[] = [];
        for (const munId of assignedMunicipalities) {
          const munRef = doc(db, 'municipalities', munId);
          const munSnap = await getDoc(munRef);
          if (munSnap.exists()) {
            municipalityNames.push(munSnap.data().name || munId);
          }
        }

        await NotificationService.sendRegionManagerInvitationNotification({
          regionManagerName: inviterName,
          regionManagerEmail: inviterData?.email || '',
          inviteeName: `${invitation.name} ${invitation.surname}`,
          inviteeEmail: invitation.email,
          inviteeRole: invitation.superAdminRole,
          assignedMunicipalities: municipalityNames,
          municipalityCount: municipalityNames.length,
          invitedAt: new Date().toISOString(),
        });
      }
      
      // If Administrator creates another Administrator, notify Super Admins
      if (
        inviterRole === SuperAdminRole.ADMINISTRATOR &&
        invitation.superAdminRole === SuperAdminRole.ADMINISTRATOR
      ) {
        await NotificationService.sendAdminCreatedNotification({
          newAdminName: `${invitation.name} ${invitation.surname}`,
          newAdminEmail: invitation.email,
          newAdminRole: 'Administrator',
          createdBy: inviterName,
          createdAt: new Date().toISOString(),
        });
      }

      // Send invitation email to the invited user via SendGrid
      // Use activation_link (same as municipality-invitation) for SendGrid template compatibility
      const baseUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_APP_URL) || 'https://fin.dimotis.com';
      const activationLink = `${baseUrl}/activate?token=${encodeURIComponent(plainToken)}`;

      try {
        const emailService = new EmailService();
        const inviteeName = `${invitation.name} ${invitation.surname}`.trim() || invitation.email;
        const invitedAt = new Date().toISOString();

        const inviteeRole = String(invitation.superAdminRole || '').toLowerCase();
        if (inviteeRole === SuperAdminRole.CONTENT_MANAGER || inviteeRole === 'content_manager') {
          await emailService.sendEmail('content-manager-invitation', {
            email: invitation.email,
            inviteeName,
            ManagerName: inviterName,
            invitedAt,
            activation_link: activationLink,
          });
        } else if (inviteeRole === SuperAdminRole.SUPPORT || inviteeRole === 'support') {
          await emailService.sendEmail('support-invitation', {
            email: invitation.email,
            inviteeName,
            ManagerName: inviterName,
            invitedAt,
            activation_link: activationLink,
          });
        } else {
          await emailService.sendEmail('region-manager-invitation', {
            email: invitation.email,
            regionManagerName: inviterName,
            inviteeName,
            inviteeEmail: invitation.email,
            inviteeRole: invitation.superAdminRole,
            invitedAt,
            activation_link: activationLink,
          });
        }
        console.log('✅ Invitation email sent to:', invitation.email);
      } catch (emailError) {
        console.error('Failed to send invitation email (non-blocking):', emailError);
        // Don't throw - email failure shouldn't block the invitation creation
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      throw error;
    }
  }

  /**
   * Update an existing super admin user
   */
  static async updateUser(
    userId: string,
    updates: SuperAdminUserUpdate
  ): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  /**
   * Delete/cancel an invitation
   */
  static async deleteInvitation(invitationId: string): Promise<void> {
    try {
      const invitationRef = doc(db, 'invitations', invitationId);
      await deleteDoc(invitationRef);
    } catch (error) {
      console.error('Error deleting invitation:', error);
      throw error;
    }
  }

  /**
   * Delete all super admin invitations for a given email
   */
  static async deleteInvitationsByEmail(email: string): Promise<void> {
    try {
      const q = query(
        collection(db, 'invitations'),
        where('email', '==', email)
      );
      const snapshot = await getDocs(q);
      const superAdminInvitations = snapshot.docs.filter(
        (d) => (d.data().role as string) === 'super_admin'
      );
      await Promise.all(
        superAdminInvitations.map((d) => deleteDoc(doc(db, 'invitations', d.id)))
      );
    } catch (error) {
      console.error('Error deleting invitations by email:', error);
      throw error;
    }
  }

  /**
   * Resend an invitation
   */
  static async resendInvitation(invitationId: string): Promise<void> {
    try {
      const invitationRef = doc(db, 'invitations', invitationId);
      
      // Update invitation status and timestamp
      await updateDoc(invitationRef, {
        invitedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        status: 'pending',
      });

      // Fetch invitation data for email
      const invitationSnap = await getDoc(invitationRef);
      if (invitationSnap.exists()) {
        const invData = invitationSnap.data();
        const plainToken = invData.tokenPlain;
        const baseUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_APP_URL) || 'https://fin.dimotis.com';
        const activationLink = plainToken ? `${baseUrl}/activate?token=${encodeURIComponent(plainToken)}` : '';

        // Resolve inviter name (ManagerName for content manager template)
        let inviterName = 'Super Admin';
        const invitedBy = invData.invitedBy;
        if (invitedBy) {
          const inviterSnap = await getDoc(doc(db, 'users', invitedBy));
          const inviterData = inviterSnap.exists() ? inviterSnap.data() : null;
          inviterName = inviterData?.displayName || inviterData?.email || inviterName;
        }

        const inviteeName = `${invData.name || ''} ${invData.surname || ''}`.trim() || invData.email || '';
        const invitedAt = new Date().toISOString();

        try {
          const emailService = new EmailService();
          const resendRole = String(invData.superAdminRole || '').toLowerCase();
          if (resendRole === SuperAdminRole.CONTENT_MANAGER || resendRole === 'content_manager') {
            await emailService.sendEmail('content-manager-invitation', {
              email: invData.email || '',
              inviteeName,
              ManagerName: inviterName,
              invitedAt,
              activation_link: activationLink,
            });
          } else if (resendRole === SuperAdminRole.SUPPORT || resendRole === 'support') {
            await emailService.sendEmail('support-invitation', {
              email: invData.email || '',
              inviteeName,
              ManagerName: inviterName,
              invitedAt,
              activation_link: activationLink,
            });
          } else {
            await emailService.sendEmail('region-manager-invitation', {
              email: invData.email || '',
              regionManagerName: inviterName,
              inviteeName,
              inviteeEmail: invData.email || '',
              inviteeRole: invData.superAdminRole || 'super_admin',
              invitedAt,
              ...(activationLink && { activation_link: activationLink }),
            });
          }
          console.log('✅ Invitation email resent to:', invData.email);
        } catch (emailError) {
          console.error('Failed to resend invitation email (non-blocking):', emailError);
        }
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      throw error;
    }
  }
}
